// ============================================================================
// APPROVAL ROUTING SECTION (which ROLE approves which approval type)
// ============================================================================
// Lives inside Configuration → Approval Rules. Each row of
// approval.tbl_approval_hierarchy names the approver role (a Keycloak role
// CODE) for one approval type. The role-scoped Approval Inbox's visibility
// filter and the server's EnsureCanAct guard both compare the JWT's roles
// against these values — changing a row here re-routes both what a role SEES
// and what it can DECIDE.
//
// It sits in Configuration rather than the Approval Inbox for three reasons:
//   1. This screen already owns the System/Property scope selector, which is
//      what per-property routing overrides need — the old inbox modal could
//      only DISPLAY a property override, never create one.
//   2. The thresholds that TRIGGER an approval (discount %, rate floor) are
//      config definitions in this same group. The limit and its approver
//      belong on one screen; split across two, you can set a threshold with no
//      idea who receives the breach.
//   3. Routing is admin-only, while the inbox is worked by whichever roles are
//      routed. An admin-only control on a shared worklist is invisible to most
//      of that screen's users.
//
// Senior-only (crs_admin / crs_system), enforced server-side on every write;
// the section simply doesn't render for anyone else. crs_system needs no rows —
// it bypasses routing entirely.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import { approvalService } from 'api/services/approvalService';
import { rolePermissionService } from 'api/services/rolePermissionService';
import notificationService from 'services/notificationService';
import { ROLES, isSeniorActor } from 'config/rolePermissions';
import type { ApprovalRoutingRow } from 'api/types/approval.types';
import type { ScopeType } from 'hooks/api/useConfigSettings';

/** Friendly labels — keep in step with the Approval Inbox's TYPE_STYLE map. */
const TYPE_LABEL: Record<string, string> = {
  DISCOUNT: 'Discount',
  COMPLIMENTARY: 'Complimentary',
  RATE_BELOW_MINIMUM: 'Rate Below Minimum',
  ROOM_UPGRADE: 'Room Upgrade',
  REFUND: 'Refund',
  ADVANCE_OVERRIDE: 'Advance Override',
  OVERDUE_ADVANCE: 'Overdue Advance',
  ARRIVAL_CHANGE: 'Arrival Change',
  FO_CHANGE_REQUEST: 'FO Request',
  NO_SHOW_PRICING: 'No-Show Pricing',
};

/**
 * The config keys in APPROVAL_RULES that decide WHEN each approval type fires.
 * Shown as a sub-line under the routing row so the limit and its approver read
 * as one sentence. Keys absent from the group are simply skipped.
 */
const TRIGGER_HINT: Record<string, { configKey: string; sentence: (v: string) => string }> = {
  DISCOUNT: {
    // The definition is seeded as `high_discount_threshold` (seed/006). The key
    // used here previously did not exist in the group, so the rule that skips
    // absent keys silently swallowed the hint and the Discount row showed no
    // threshold at all.
    configKey: 'high_discount_threshold',
    sentence: (v) => `Triggered when a discount is ${v}% or more`,
  },
  ARRIVAL_CHANGE: {
    configKey: 'block_checkout_on_unpriced_arrival_change',
    sentence: (v) =>
      v.toLowerCase() === 'true'
        ? 'Blocks check-out until priced'
        : 'Does not block check-out (gate is off)',
  },
};

/**
 * The APPROVAL_RULES toggle that decides whether each type fires AT ALL.
 *
 * Routing answers "who approves this"; these answer "is it approved at all".
 * A role picked for a type whose toggle is off routes nothing, so the row is
 * shown but disabled rather than silently accepting a setting with no effect.
 *
 * Types absent from this map have no on/off switch — they are raised by the
 * business flow itself (a front-office request, an advance override, a no-show
 * pricing decision) and always need an approver.
 */
const REQUIRED_BY: Record<string, { configKey: string; label: string }> = {
  DISCOUNT: { configKey: 'discount_approval_required', label: 'Discount Approval Required' },
  COMPLIMENTARY: { configKey: 'comp_approval_required', label: 'Comp Approval Required' },
  ROOM_UPGRADE: { configKey: 'room_upgrade_requires_approval', label: 'Room Upgrade Approval Required' },
};

/** One editable line in the section — an existing row, or a staged new override. */
interface RoutingSlot {
  /** Stable key: the row id for an existing row, `new:TYPE` for a staged override. */
  key: string;
  approvalType: string;
  /** The row this slot edits. Null when the slot is an unsaved property override. */
  row: ApprovalRoutingRow | null;
  /** Role in effect right now — the override's role, else the inherited global role. */
  effectiveRole: string;
  /** True when the effective role comes from the global row, not a property override. */
  inherited: boolean;
  approvalLevel: number;
  pinnedUser: boolean;
}

interface ApprovalRoutingSectionProps {
  scopeType: ScopeType;
  selectedPropertyId: string | null;
  /**
   * Reported upward so ConfigSettingsScreen's group/scope-change guard covers
   * routing edits too. Without it, switching property with a pending role
   * change would silently drop it.
   */
  onDirtyChange: (dirty: boolean) => void;
  /**
   * Bumped by the screen when the user discards changes. Watching a counter
   * (rather than a boolean) means repeated discards each land.
   */
  discardSignal: number;
  /** Live config values in this group, used for the TRIGGER_HINT sub-lines. */
  liveValue: (configKey: string) => string | undefined;
}

const ApprovalRoutingSection: React.FC<ApprovalRoutingSectionProps> = ({
  scopeType,
  selectedPropertyId,
  onDirtyChange,
  discardSignal,
  liveValue,
}) => {
  const isSenior = useMemo(() => isSeniorActor(), []);

  const [rows, setRows] = useState<ApprovalRoutingRow[]>([]);
  const [roleOptions, setRoleOptions] = useState<{ value: string; label: string }[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [removeTarget, setRemoveTarget] = useState<ApprovalRoutingRow | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [routing, roles] = await Promise.all([
        approvalService.getRouting(),
        rolePermissionService.getAll({ page: 1, pageSize: 100 }),
      ]);
      setRows(routing.data ?? []);
      setRoleOptions(
        (roles.data ?? [])
          .filter((r: any) => r.roleCode && r.roleCode !== ROLES.SYSTEM)
          .map((r: any) => ({ value: r.roleCode, label: `${r.roleName} (${r.roleCode})` })),
      );
    } catch {
      setError('Failed to load the approval routing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSenior) load();
  }, [isSenior, load]);

  // Scope or property change abandons staged edits — the slots they referred to
  // are no longer on screen. The screen's guard has already asked the user.
  useEffect(() => {
    setEdits({});
  }, [scopeType, selectedPropertyId]);

  useEffect(() => {
    if (discardSignal > 0) setEdits({});
  }, [discardSignal]);

  const dirtyCount = Object.keys(edits).length;
  useEffect(() => {
    onDirtyChange(dirtyCount > 0);
  }, [dirtyCount, onDirtyChange]);

  /**
   * Clear the screen's flag on unmount. Switching away from Approval Rules
   * unmounts this section in the same commit that the discard runs, so the
   * effect above never gets to report `false` — and the screen would then think
   * routing was dirty forever, raising an Unsaved Changes dialog on every
   * subsequent group or scope change.
   */
  useEffect(() => () => { onDirtyChange(false); }, [onDirtyChange]);

  /**
   * Global rows are the canonical list of approval types — an override can only
   * ever refine one of them (the server enforces the same rule), so they drive
   * the rendering in both scopes.
   */
  const globalRows = useMemo(
    () =>
      rows
        .filter((r) => !r.propertyId)
        .sort((a, b) =>
          (TYPE_LABEL[a.approvalType] ?? a.approvalType).localeCompare(
            TYPE_LABEL[b.approvalType] ?? b.approvalType,
          ),
        ),
    [rows],
  );

  const slots = useMemo<RoutingSlot[]>(() => {
    if (scopeType === 'SYSTEM') {
      return globalRows.map((r) => ({
        key: r.id,
        approvalType: r.approvalType,
        row: r,
        effectiveRole: r.approverRole,
        inherited: false,
        approvalLevel: r.approvalLevel,
        pinnedUser: !!r.approverUserId,
      }));
    }
    if (!selectedPropertyId) return [];
    return globalRows.map((g) => {
      const override = rows.find(
        (r) => r.propertyId === selectedPropertyId && r.approvalType === g.approvalType,
      );
      return override
        ? {
            key: override.id,
            approvalType: override.approvalType,
            row: override,
            effectiveRole: override.approverRole,
            inherited: false,
            approvalLevel: override.approvalLevel,
            pinnedUser: !!override.approverUserId,
          }
        : {
            key: `new:${g.approvalType}`,
            approvalType: g.approvalType,
            row: null,
            effectiveRole: g.approverRole,
            inherited: true,
            approvalLevel: g.approvalLevel,
            pinnedUser: false,
          };
    });
  }, [scopeType, selectedPropertyId, globalRows, rows]);

  const handleSave = useCallback(async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    let saved = 0;
    try {
      for (const slot of slots) {
        const next = edits[slot.key];
        if (!next || next === slot.effectiveRole) continue;

        if (slot.row) {
          await approvalService.updateRouting(slot.row.id, {
            approverRole: next,
            rowVersion: slot.row.rowVersion,
          });
        } else {
          // Staged property override — the first save materialises the row.
          await approvalService.createRoutingOverride({
            propertyId: selectedPropertyId as string,
            approvalType: slot.approvalType,
            approverRole: next,
          });
        }
        saved++;
      }
      setEdits({});
      await load();
      notificationService.toast.success(
        `Approval routing updated (${saved} change${saved !== 1 ? 's' : ''}).`,
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Failed to save the approval routing.';
      notificationService.toast.error(
        saved > 0
          ? `${msg} (${saved} change${saved !== 1 ? 's' : ''} already saved.)`
          : msg,
      );
      // Partial failure: reload so row_versions are fresh for a retry.
      await load();
      setEdits({});
    } finally {
      setSaving(false);
    }
  }, [dirtyCount, slots, edits, selectedPropertyId, load]);

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await approvalService.retireRoutingOverride(removeTarget.id, removeTarget.rowVersion);
      setRemoveTarget(null);
      setEdits({});
      await load();
      notificationService.toast.success(
        `Override removed — ${TYPE_LABEL[removeTarget.approvalType] ?? removeTarget.approvalType} ` +
          'now follows the all-properties routing.',
      );
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message || err?.message || 'Failed to remove the override.',
      );
    } finally {
      setRemoving(false);
    }
  }, [removeTarget, load]);

  if (!isSenior) return null;

  const sectionStyle: React.CSSProperties = {
    margin: '0 1.25rem',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    background: 'var(--bg-secondary)',
    overflow: 'hidden',
  };

  return (
    <div style={sectionStyle}>
      {/* Section header — no longer collapsible. The Approval Routing TAB is
          the disclosure now; a chevron inside it would hide the whole tab's
          contents behind a second one, and the title would repeat the tab
          label back at the reader. */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.6rem 0.85rem',
          borderBottom: '1px solid var(--border-color)',
        }}
      >
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
          {scopeType === 'SYSTEM'
            ? 'Who approves each type, all properties'
            : 'Per-property overrides'}
        </span>
        {dirtyCount > 0 && (
          <span
            style={{
              fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.4rem',
              borderRadius: '999px', background: 'var(--primary)', color: '#fff',
            }}
          >
            {dirtyCount} unsaved
          </span>
        )}
      </div>

        <div style={{ padding: '0.7rem 0.85rem' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.6rem', lineHeight: 1.5 }}>
            Sets which role approves each type — this drives both what a role
            <b> sees</b> in the Approval Inbox and what it can <b>decide</b>. The
            administrator role always sees everything via the inbox's All-items
            view, and the system role bypasses routing entirely.
          </div>

          {error && <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{error}</div>}
          {loading && (
            <div style={{ padding: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Loading routing…
            </div>
          )}

          {/* Routing has no rate-plan level — say so rather than showing an empty box. */}
          {!loading && scopeType === 'RATE_PLAN' && (
            <div style={{ padding: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Approval routing is set per property, not per rate plan. Switch to
              System Defaults or Property Override to edit it.
            </div>
          )}

          {!loading && scopeType === 'PROPERTY' && !selectedPropertyId && (
            <div style={{ padding: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Select a property to view or add its routing overrides.
            </div>
          )}

          {!loading && scopeType !== 'RATE_PLAN' && slots.length > 0 && slots.map((slot) => {
            const hint = TRIGGER_HINT[slot.approvalType];
            const hintValue = hint ? liveValue(hint.configKey) : undefined;
            const staged = edits[slot.key];
            const value = staged ?? slot.effectiveRole;

            // Is this type switched on at all? Read from the live config so an
            // unsaved toggle above updates the row immediately — the admin sees
            // the consequence of the switch before saving either half.
            const gate = REQUIRED_BY[slot.approvalType];
            const notRequired = gate
              ? (liveValue(gate.configKey) ?? 'true').toLowerCase() !== 'true'
              : false;
            return (
              <div
                key={slot.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.4rem 0.6rem', marginBottom: '0.3rem',
                  background: 'var(--bg-primary)', borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.78rem',
                }}
              >
                <div style={{ flex: 1, minWidth: 0, opacity: notRequired ? 0.55 : 1 }}>
                  <b style={{ color: 'var(--text-primary)' }}>
                    {TYPE_LABEL[slot.approvalType] ?? slot.approvalType}
                  </b>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                    {notRequired ? (
                      <>Not required — turn on &ldquo;{gate!.label}&rdquo; on the Approval Rules tab</>
                    ) : slot.inherited ? (
                      <>Inherited from all-properties routing · pick a role to override</>
                    ) : (
                      <>
                        {scopeType === 'SYSTEM' ? 'All properties' : 'Property override'}
                        {' · level '}{slot.approvalLevel}
                        {slot.pinnedUser ? ' · pinned to a specific user' : ''}
                      </>
                    )}
                    {!notRequired && hint && hintValue ? ` · ${hint.sentence(hintValue)}` : ''}
                  </div>
                </div>

                {/* Active/inactive says plainly whether this routing is doing
                    anything. Dimming alone reads as "disabled control", not as
                    "this type is switched off in the rules". */}
                <span
                  style={{
                    fontSize: '0.62rem', fontWeight: 700, padding: '0.1rem 0.45rem',
                    borderRadius: '999px', whiteSpace: 'nowrap', flexShrink: 0,
                    background: notRequired ? 'var(--bg-tertiary)' : 'rgba(22,163,74,0.15)',
                    color: notRequired ? 'var(--text-muted)' : 'var(--success, #16a34a)',
                    border: `1px solid ${notRequired ? 'var(--border-color)' : 'rgba(22,163,74,0.35)'}`,
                  }}
                  title={notRequired
                    ? `Switched off by "${gate!.label}" on the Approval Rules tab`
                    : 'This type routes for approval'}
                >
                  {notRequired ? 'INACTIVE' : 'ACTIVE'}
                </span>

                {slot.inherited && !notRequired && (
                  <span
                    style={{
                      fontSize: '0.62rem', fontWeight: 700, padding: '0.1rem 0.4rem',
                      borderRadius: '999px', border: '1px solid var(--border-color)',
                      color: 'var(--text-muted)', whiteSpace: 'nowrap',
                    }}
                  >
                    INHERITED
                  </span>
                )}

                <div style={{ width: '14rem', flexShrink: 0 }}>
                  <SyutSelect
                    options={roleOptions}
                    value={value}
                    onChange={(v) =>
                      setEdits((prev) => {
                        const picked = (v as string) || slot.effectiveRole;
                        const next = { ...prev };
                        // Selecting the role already in effect is not an edit.
                        if (picked === slot.effectiveRole) delete next[slot.key];
                        else next[slot.key] = picked;
                        return next;
                      })
                    }
                    isClearable={false}
                    // Disabled rather than hidden: the routing stays visible and
                    // is restored intact when the toggle goes back on, and a
                    // vanishing row would make the list jump as toggles change.
                    isDisabled={saving || notRequired}
                    size="sm"
                    menuPortalTarget={document.body}
                    // A routing row may carry a code with no matching role row
                    // (e.g. a renamed or legacy role) — keep it selectable so
                    // the control never renders blank.
                    seedOptions={
                      roleOptions.some((o) => o.value === value)
                        ? undefined
                        : [{ value, label: value }]
                    }
                  />
                </div>

                {/* Only overrides can be removed; the global row is the fallback. */}
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{
                    padding: '0.25rem 0.5rem', fontSize: '0.7rem',
                    visibility: slot.row && slot.row.propertyId ? 'visible' : 'hidden',
                  }}
                  disabled={saving}
                  onClick={() => setRemoveTarget(slot.row)}
                  title="Remove this override and fall back to the all-properties routing"
                >
                  Remove
                </button>
              </div>
            );
          })}

          {!loading && scopeType !== 'RATE_PLAN' && slots.length === 0
            && !(scopeType === 'PROPERTY' && !selectedPropertyId) && (
            <div style={{ padding: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              No approval types are configured. Seed approval.tbl_approval_hierarchy first.
            </div>
          )}

          {dirtyCount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.6rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEdits({})}
                disabled={saving}
              >
                Discard Routing Changes
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? 'Saving…'
                  : `Save Routing (${dirtyCount})`}
              </button>
            </div>
          )}
        </div>

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove Routing Override?"
        message={
          removeTarget
            ? `"${TYPE_LABEL[removeTarget.approvalType] ?? removeTarget.approvalType}" will fall back to the ` +
              'all-properties routing for this property. Existing pending approvals re-route immediately.'
            : ''
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        loading={removing}
        loadingMessage="Removing..."
        onConfirm={handleConfirmRemove}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};

export default ApprovalRoutingSection;
